import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, getViewAs, VIEW_AS_KEY } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [viewAs, setViewAs] = useState(getViewAs);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const startViewAs = useCallback((client) => {
    const v = { id: client.id, company_name: client.company_name };
    sessionStorage.setItem(VIEW_AS_KEY, JSON.stringify(v));
    setViewAs(v);
  }, []);

  const stopViewAs = useCallback(() => {
    sessionStorage.removeItem(VIEW_AS_KEY);
    setViewAs(null);
  }, []);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => {});
    stopViewAs();
    setUser(false);
  }, [stopViewAs]);

  const isViewingAs = !!(user && user.role === "admin" && viewAs);
  const effectiveRole = isViewingAs ? "client" : user?.role;
  const companyName = isViewingAs ? viewAs.company_name : user?.client?.company_name;

  const value = useMemo(
    () => ({ user, setUser, refresh, logout, viewAs, isViewingAs, effectiveRole, companyName, startViewAs, stopViewAs }),
    [user, refresh, logout, viewAs, isViewingAs, effectiveRole, companyName, startViewAs, stopViewAs]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
